import { z } from 'zod';
/**
 * Auth request schemas — phases.md Phase 3, §2 ("every request body passes through a Zod
 * schema from packages/shared"). These drive both the API's validation middleware and the
 * admin login/change-password forms, so the two can never silently disagree on what a valid
 * password looks like.
 */
const email = z.string().trim().toLowerCase().email('Enter a valid email address');
/** Applies to a *new* password only. The login schema below deliberately does not reuse
 * this — an existing password predates the policy and must still be checkable. */
const newPassword = z.string().min(8, 'Password must be at least 8 characters');
export const loginSchema = z.object({
    email,
    password: z.string().min(1, 'Password is required'),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword,
});
export const forgotPasswordSchema = z.object({ email });
export const resetPasswordSchema = z.object({
    token: z.string().min(1, 'Reset token is required'),
    newPassword,
});
/**
 * User administration — PRD §24, phases.md Stage 14d.
 *
 * A new account is created **without a password**: the admin sets a temporary one and
 * the account carries `mustChangePassword`, which Phase 3's login already blocks on. An
 * administrator typing a colleague's permanent password into a form is the workflow
 * this avoids.
 */
export const userCreateSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(255),
    fullName: z.string().trim().min(1).max(160),
    roleId: z.number().int().positive(),
    preferredLocale: z.enum(['as', 'en']).default('as'),
    /** The one-time password the admin hands over; the account must change it at login. */
    temporaryPassword: z.string().min(12).max(200),
});
export const userUpdateSchema = z.object({
    fullName: z.string().trim().min(1).max(160).optional(),
    email: z.string().trim().toLowerCase().email().max(255).optional(),
    roleId: z.number().int().positive().optional(),
    preferredLocale: z.enum(['as', 'en']).optional(),
    status: z.enum(['active', 'invited', 'suspended', 'deactivated']).optional(),
    password: z.string().min(8).max(200).optional(),
});
export const profileUpdateSchema = z.object({
    fullName: z.string().trim().min(1).max(160).optional(),
    email: z.string().trim().toLowerCase().email().max(255).optional(),
    preferredLocale: z.enum(['as', 'en']).optional(),
});
export const forcePasswordResetSchema = z.object({
    temporaryPassword: z.string().min(12).max(200),
});
/** The audit viewer's filters — PRD §30. */
export const auditQuerySchema = z.object({
    actorId: z.coerce.number().int().positive().optional(),
    action: z.string().trim().max(80).optional(),
    entityType: z.string().trim().max(60).optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    page: z.coerce.number().int().positive().default(1),
    perPage: z.coerce.number().int().positive().max(100).default(25),
});
/**
 * Site settings are key/value rows (`site_settings`), so the write is a map rather than
 * a fixed object: the settings screen sends what it changed, and a key the newsroom
 * invents later needs no schema change.
 */
export const settingsUpdateSchema = z.object({
    settings: z.record(z.string().min(1).max(100), z.unknown()),
    language: z.enum(['as', 'en']).nullish(),
});
export const seoSettingsUpdateSchema = z.object({
    language: z.enum(['as', 'en']),
    defaultTitle: z.string().trim().max(200).nullish(),
    titleTemplate: z.string().trim().max(200).nullish(),
    defaultDescription: z.string().trim().max(400).nullish(),
    organizationName: z.string().trim().max(200).nullish(),
    twitterSite: z.string().trim().max(80).nullish(),
    robotsDirectives: z.string().trim().max(200).nullish(),
    googleVerification: z.string().trim().max(200).nullish(),
    bingVerification: z.string().trim().max(200).nullish(),
});
//# sourceMappingURL=auth.js.map